/** The texts of benchmarks (BEN-*), spread into the app's dictionaries (`lib/i18n.tsx`). */
export const benchmarksEn = {
  benchmarks: 'Benchmarks',
  benchmarksIntro:
    'Benchmarks score models against the test images of a frozen collection. Participants receive the images without annotations, submit their predictions and see aggregate scores; the reference never leaves the platform.',
  benchmarksNone: 'No benchmark yet. A coordinator creates one on the page of a frozen collection.',
  benchmarkCreate: 'Create a benchmark',
  benchmarkCreateHint:
    'From the test split of this frozen collection: the images that hold the chosen reference and that their sites allow processing, in random order under case numbers.',
  benchmarkReaders: 'The readers’ sets, each on its own',
  benchmarkMetrics: 'Metrics',
  metric_classification: 'Classification: sensitivity, specificity, ROC-AUC',
  metric_detection: 'Detection: precision, recall, mAP, FROC',
  benchmarkWeekly: 'Submissions per participant and week',
  benchmarkTerms: 'Data use terms',
  benchmarkTermsHint: 'Participants accept them before any image.',
  benchmarkTermsDefault:
    'The images of this benchmark are for scoring models only. Do not annotate them, do not train or tune any model on them and do not pass them on. Delete them when the benchmark ends.',
  benchmarkCases: '{count} cases',
  benchmarkCases_one: '1 case',
  benchmarkWithdrawn: 'Withdrawn: its frozen collection is no longer available',
  benchmarkSplitReview: 'Results to review',
  benchmarkSplitReviewHint: 'Its frozen collection turned out to hold one patient in two sets, so a model may have trained on images of a test patient. Review the results before relying on them.',
  benchmarkParticipants: 'Participants',
  benchmarkInvite: 'Invite',
  benchmarkInviteHint:
    'Every site whose images the benchmark holds decides by its sharing policy. Those who see the frozen collection’s annotations cannot take part.',
  benchmarkSubmissionCount: '{count} submissions',
  benchmarkSubmissionCount_one: '1 submission',
  benchmarkRevoke: 'End participation',
  benchmarkAccept: 'Accept the terms and take part',
  benchmarkWaiting: 'Not every site whose images the benchmark holds has approved yet.',
  benchmarkRefused: 'A site whose images the benchmark holds refused.',
  benchmarkDownload: 'Download all cases (ZIP)',
  benchmarkDownloadHint:
    'Each case is a DICOM file with its pixel values and the values that display them, under a number valid only in this benchmark. The files carry a mark in their pixel values that identifies your account.',
  benchmarkSubmit: 'Submit predictions',
  benchmarkFile: 'Predictions (JSON)',
  benchmarkModel: 'Model',
  benchmarkVersion: 'Version',
  benchmarkShown: 'On the leaderboard',
  shown_hidden: 'Not shown',
  shown_named: 'Shown with my name',
  shown_anonymous: 'Shown anonymously',
  benchmarkRemaining: '{count} submissions left this week',
  benchmarkRemaining_one: '1 submission left this week',
  benchmarkMySubmissions: 'My submissions',
  benchmarkSmallGroups: 'Groups of fewer than 20 images are left out, and no result names a single image.',
  benchmarkLeaderboard: 'Leaderboard',
  benchmarkLeaderboardHint:
    'The submissions their authors show and the platform’s released model versions, ranked by mAP, then by mean ROC-AUC.',
  benchmarkAnonymous: 'Anonymous',
  benchmarkPlatform: 'Released on the platform',
  benchmarkMine: 'mine',
  benchmarkMeanAuc: 'Mean ROC-AUC',
  benchmarkFormat: 'Format of the predictions',
  benchmarkFormatText:
    'A JSON object with one entry per case number, each a list of findings: the label, a confidence from 0 to 1, and a box [x1, y1, x2, y2] or a polygon [x, y, …] in the image’s pixels. A finding without box or polygon scores the whole image. A case without findings gets an empty list.',
  benchmarkInvalidFile: 'The file is not a JSON object keyed by case number.',
  benchmarkApprovals: 'Benchmarks waiting for approval',
  benchmarkApprovalsHint:
    'A participant would receive these images of the site, without annotations, patient numbers or dates, to score their model.',
  benchmarkApprovalCases: '{count} of the site’s images',
  benchmarkApprovalCases_one: '1 of the site’s images',
  benchmarkNoneWaiting: 'No benchmark waits for the site.',
  kind_benchmark_approval: 'A benchmark waits for my site’s approval',
  notice_benchmark_approval: '{benchmark}: a participant would receive images of {site}',
  kind_benchmark_invited: 'I am invited to a benchmark',
  notice_benchmark_invited: 'You are invited to the benchmark {benchmark}',
}

export const benchmarksDe: Record<keyof typeof benchmarksEn, string> = {
  benchmarks: 'Benchmarks',
  benchmarksIntro:
    'Benchmarks bewerten Modelle an den Testbildern einer eingefrorenen Sammlung. Teilnehmende erhalten die Bilder ohne Annotationen, reichen ihre Vorhersagen ein und sehen zusammengefasste Ergebnisse; die Referenz verlässt die Plattform nie.',
  benchmarksNone: 'Noch kein Benchmark. Koordinierende erstellen ihn auf der Seite einer eingefrorenen Sammlung.',
  benchmarkCreate: 'Benchmark erstellen',
  benchmarkCreateHint:
    'Aus dem Testanteil dieser eingefrorenen Sammlung: die Bilder mit der gewählten Referenz, deren Standorte die Verarbeitung erlauben, in zufälliger Reihenfolge unter Fallnummern.',
  benchmarkReaders: 'Die Sätze der Befundenden, jeder für sich',
  benchmarkMetrics: 'Kennzahlen',
  metric_classification: 'Klassifikation: Sensitivität, Spezifität, ROC-AUC',
  metric_detection: 'Detektion: Präzision, Recall, mAP, FROC',
  benchmarkWeekly: 'Einreichungen pro Teilnahme und Woche',
  benchmarkTerms: 'Nutzungsbedingungen',
  benchmarkTermsHint: 'Teilnehmende akzeptieren sie vor dem ersten Bild.',
  benchmarkTermsDefault:
    'Die Bilder dieses Benchmarks dienen nur der Bewertung von Modellen. Annotieren Sie sie nicht, trainieren oder optimieren Sie kein Modell mit ihnen und geben Sie sie nicht weiter. Löschen Sie sie, wenn der Benchmark endet.',
  benchmarkCases: '{count} Fälle',
  benchmarkCases_one: '1 Fall',
  benchmarkWithdrawn: 'Zurückgezogen: Seine eingefrorene Sammlung ist nicht mehr verfügbar',
  benchmarkSplitReview: 'Ergebnisse prüfen',
  benchmarkSplitReviewHint: 'Seine eingefrorene Sammlung enthält, wie sich herausstellte, eine Patientin oder einen Patienten in zwei Mengen, sodass ein Modell mit Bildern einer Testperson trainiert worden sein kann. Prüfen Sie die Ergebnisse, bevor Sie sich auf sie stützen.',
  benchmarkParticipants: 'Teilnehmende',
  benchmarkInvite: 'Einladen',
  benchmarkInviteHint:
    'Jeder Standort, dessen Bilder der Benchmark enthält, entscheidet nach seiner Freigaberichtlinie. Wer die Annotationen der eingefrorenen Sammlung sieht, kann nicht teilnehmen.',
  benchmarkSubmissionCount: '{count} Einreichungen',
  benchmarkSubmissionCount_one: '1 Einreichung',
  benchmarkRevoke: 'Teilnahme beenden',
  benchmarkAccept: 'Bedingungen akzeptieren und teilnehmen',
  benchmarkWaiting: 'Noch haben nicht alle Standorte zugestimmt, deren Bilder der Benchmark enthält.',
  benchmarkRefused: 'Ein Standort, dessen Bilder der Benchmark enthält, hat abgelehnt.',
  benchmarkDownload: 'Alle Fälle herunterladen (ZIP)',
  benchmarkDownloadHint:
    'Jeder Fall ist eine DICOM-Datei mit ihren Pixelwerten und den Werten zu ihrer Darstellung, unter einer Nummer, die nur in diesem Benchmark gilt. Die Dateien tragen in ihren Pixelwerten eine Markierung, die Ihr Konto erkennen lässt.',
  benchmarkSubmit: 'Vorhersagen einreichen',
  benchmarkFile: 'Vorhersagen (JSON)',
  benchmarkModel: 'Modell',
  benchmarkVersion: 'Version',
  benchmarkShown: 'In der Rangliste',
  shown_hidden: 'Nicht gezeigt',
  shown_named: 'Mit meinem Namen gezeigt',
  shown_anonymous: 'Anonym gezeigt',
  benchmarkRemaining: 'Noch {count} Einreichungen diese Woche',
  benchmarkRemaining_one: 'Noch 1 Einreichung diese Woche',
  benchmarkMySubmissions: 'Meine Einreichungen',
  benchmarkSmallGroups: 'Gruppen mit weniger als 20 Bildern fehlen, und kein Ergebnis nennt ein einzelnes Bild.',
  benchmarkLeaderboard: 'Rangliste',
  benchmarkLeaderboardHint:
    'Die Einreichungen, die ihre Urheber zeigen, und die freigegebenen Modellversionen der Plattform, geordnet nach mAP, dann nach mittlerer ROC-AUC.',
  benchmarkAnonymous: 'Anonym',
  benchmarkPlatform: 'Auf der Plattform freigegeben',
  benchmarkMine: 'meine',
  benchmarkMeanAuc: 'Mittlere ROC-AUC',
  benchmarkFormat: 'Format der Vorhersagen',
  benchmarkFormatText:
    'Ein JSON-Objekt mit einem Eintrag je Fallnummer, jeweils eine Liste von Befunden: das Label, eine Konfidenz von 0 bis 1 und eine Box [x1, y1, x2, y2] oder ein Polygon [x, y, …] in den Pixeln des Bildes. Ein Befund ohne Box oder Polygon bewertet das ganze Bild. Ein Fall ohne Befunde erhält eine leere Liste.',
  benchmarkInvalidFile: 'Die Datei ist kein JSON-Objekt nach Fallnummern.',
  benchmarkApprovals: 'Benchmarks, die auf Zustimmung warten',
  benchmarkApprovalsHint:
    'Eine Teilnahme würde diese Bilder des Standorts ohne Annotationen, Patientennummern oder Daten erhalten, um ein Modell zu bewerten.',
  benchmarkApprovalCases: '{count} Bilder des Standorts',
  benchmarkApprovalCases_one: '1 Bild des Standorts',
  benchmarkNoneWaiting: 'Kein Benchmark wartet auf den Standort.',
  kind_benchmark_approval: 'Ein Benchmark wartet auf die Zustimmung meines Standorts',
  notice_benchmark_approval: '{benchmark}: Eine Teilnahme würde Bilder von {site} erhalten',
  kind_benchmark_invited: 'Ich bin zu einem Benchmark eingeladen',
  notice_benchmark_invited: 'Sie sind zum Benchmark {benchmark} eingeladen',
}
